import { Country, PersonalInformation } from "../../model/PersonalInformationType";
import { isInFuture, isInPast, isOutsideYearRange, isValidDate } from "../../util/dateUtil";
import { UKRAJINA_OZNAKA } from "../../common/data/sifarnik";

const isEmptySelection = (value: unknown): boolean =>
  !value || (typeof value === "object" && Object.keys(value as object).length === 0);

export const validateField = (
  field: string,
  value: string | Country | boolean | null,
  setFieldError: (field: string, message: (string | undefined)) => void,
): boolean => {
    let error: string | undefined = undefined;

    if (field === 'spol') {
        const stringValue = typeof value === 'string' ? value : '';

        if (!stringValue.trim()) {
            error = "common.validation-error.required";
        }
    } else if (field === 'ime') {
        const stringValue = typeof value === 'string' ? value : '';
        const firstNameRegex = /^[a-zA-ZčćžđšČĆŽĐŠ\- ]+$/;

        if (!stringValue.trim()) {
            error = "common.validation-error.required";
        } else if (!firstNameRegex.test(stringValue)) {
            error = 'common.validation-error.type.letter-space-line';
        }
    } else if (field === 'prezime') {
        const stringValue = typeof value === 'string' ? value : '';
        const lastNameRegex = /^[a-zA-ZčćžđšČĆŽĐŠ\- ]+$/;

        if (!stringValue.trim()) {
            error = "common.validation-error.required";
        } else if (!lastNameRegex.test(stringValue)) {
            error = 'common.validation-error.type.letter-space-line';
        }
    } else if (field === 'datumRodjenja') {
        const stringValue = typeof value === 'string' ? value : '';
        const numericOnly = stringValue.replace(/\./g, '');
        const dateOfBirthRegex = /^[0-9]+$/;

        if (!stringValue.trim()) {
            error = "common.validation-error.required";
        } else if (!dateOfBirthRegex.test(numericOnly)) {
            error = 'common.validation-error.type.number';
        } else if (numericOnly.length !== 8) {
            error = 'common.validation-error.type.date.general';
        } else if (!isValidDate(numericOnly)) {
            error = 'common.validation-error.type.date.general';
        } else if (isInFuture(numericOnly)) {
            error = 'common.validation-error.type.date.future';
        } else if (isOutsideYearRange(numericOnly, 120, 0)) {
            error = 'personal-information-page.personal-information.date-of-birth-field.validation-error.outside-specific-range';
        }
    } else if (field === 'mjestoRodjenja') {
        const stringValue = typeof value === 'string' ? value : '';
        const placeOfBirthRegex = /^[a-zA-ZčćžđšČĆŽĐŠ ]+$/;

        if (!stringValue.trim()) {
            error = "common.validation-error.required";
        } else if (!placeOfBirthRegex.test(stringValue)) {
            error = 'common.validation-error.type.letter-space';
        }
    } else if (field === 'drzavaRodjenja') {
        if (isEmptySelection(value)) {
            error = "common.validation-error.required";
        }
    } else if (field === 'drzavljanstvo') {
        if (isEmptySelection(value)) {
            error = "common.validation-error.required";
        }
    } else if (field === 'drzavljanstvoDrugo') {
        if (isEmptySelection(value)) {
            error = "common.validation-error.required";
        }
    } else if (field === 'idDokumentBroj') {
        const stringValue = typeof value === 'string' ? value : '';
        const idPatternRegex = /^[a-zA-ZčćžđšČĆŽĐŠ0-9]+$/;

        if (!stringValue.trim()) {
            error = "common.validation-error.required";
        } else if (!idPatternRegex.test(stringValue)) {
          error = "common.validation-error.type.number-letter";
        } else if (stringValue.trim().length < 6) {
            error = "personal-information-page.identification-document.number-field.validation-error.min-length";
        } else if (stringValue.trim().length > 13) {
            error = "personal-information-page.identification-document.number-field.validation-error.max-length";
        }
    } else if (field === 'idDokumentIzdavatelj') {
        const stringValue = typeof value === 'string' ? value : '';

        if (!stringValue.trim()) {
            error = "common.validation-error.required";
        }
    } else if (field === 'idDokumentDrzavaIzdavanja') {
        if (isEmptySelection(value)) {
            error = "common.validation-error.required";
        }
    } else if (
      field === 'drzavaRodjenjaRegija' ||
      field === 'drzavljanstvoRegija' ||
      field === 'drzavljanstvoDrugoRegija' ||
      field === 'idDokumentDrzavaIzdavanjaRegija'
    ) {
        if (isEmptySelection(value)) {
            error = "common.validation-error.required";
        }
    } else if (field === 'idDokumentDatumVazenja') {
        const stringValue = typeof value === 'string' ? value : '';
        const numericOnly = stringValue.replace(/\./g, '');
        const dateOfExpiryRegex = /^[0-9]+$/;

        if (!stringValue.trim()) {
            error = "common.validation-error.required";
        } else if (!dateOfExpiryRegex.test(numericOnly)) {
            error = 'common.validation-error.type.number';
        } else if (numericOnly.length !== 8) {
            error = "personal-information-page.identification-document.expiration-date-field.validation-error.exact-length";
        } else {
            if (!isValidDate(numericOnly)) {
              error = 'common.validation-error.type.date.general'
            } else if (isInPast(numericOnly)) {
                error = "personal-information-page.identification-document.expiration-date-field.validation-error.past-date";
            } else if (isOutsideYearRange(numericOnly, 60, 60)) {
                error = "personal-information-page.identification-document.expiration-date-field.validation-error.max-date";
            }
        }

    }
    setFieldError(field, error);

    return error === undefined;
};

export const validateForm = (values: PersonalInformation, setFieldError: (field: string, message: (string | undefined)) => void): boolean => {
    let isFormValid = true;
    Object.entries(values).map(([field, value]) => {
        const currentField = field as keyof PersonalInformation;

        if (
          currentField === "dostavaMjesto" ||
          currentField === "dostavaDrzava" ||
          currentField === "dostavaPostanskiBroj" ||
          currentField === "dostavaKucniBr" ||
          currentField === "dostavaUlica"
        ) {
            if (!values.dostavaDrugaAdresa) {
                return;
            }
        } else if (currentField === "idDokumentDatumVazenja") {
            if (values.idDokumentTrajnoVazenje) {
                return;
            }
        } else if (currentField === "drzavljanstvoDrugo") {
            if (!values.imaDrugoDrzavljanstvo) {
                return;
            }
        } else if (currentField === "drzavaRodjenjaRegija") {
            if (values.drzavaRodjenja?.oznaka !== UKRAJINA_OZNAKA) {
                return;
            }
        } else if (currentField === "drzavljanstvoRegija") {
            if (values.drzavljanstvo?.oznaka !== UKRAJINA_OZNAKA) {
                return;
            }
        } else if (currentField === "drzavljanstvoDrugoRegija") {
            if (!values.imaDrugoDrzavljanstvo || values.drzavljanstvoDrugo?.oznaka !== UKRAJINA_OZNAKA) {
                return;
            }
        } else if (currentField === "idDokumentDrzavaIzdavanjaRegija") {
            if (values.idDokumentDrzavaIzdavanja?.oznaka !== UKRAJINA_OZNAKA) {
                return;
            }
        }

        if (!validateField(field, value, setFieldError)) {
            isFormValid = false;
        }
    });

    return isFormValid;
};
